// Batch-rename modal for press-kit files.
//
// The preview is the whole safety argument. It is recomputed from
// renameEngine on every keystroke — the same code that builds the payload — so
// what you approve and what gets sent cannot disagree. Anything the engine
// marks as a collision or invalid blocks the apply outright rather than being
// skipped quietly, because a rename is the one press-kit operation whose
// result does not tell you what it used to be.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { S, GOLD, CommsModal, Field, FilterBtn, BtnGhost } from './commsUi';
import { callEdgeFunction, listFolder, invalidateListing, renameCachedEntries } from './pressAssets';
import { buildRenamePlan, planSummary, suggestSeqStart, hasSequenceToken, TOKENS } from './renameEngine';

// Tolerant of a missing path on purpose: this reads server results as well as
// planned rows, and a result the server could not locate carries neither path.
// Throwing here would take down the very report that was meant to name it.
const baseName = p => String(p || '').slice(String(p || '').lastIndexOf('/') + 1);
const mono = "'IBM Plex Mono', monospace";

// The standard naming protocol:
//
//   Architecture-BRIO_FULL-PROJECT-NAME_PROJECT-LOCATION_PHOTOGRAPHER-NAME_
//   NEW-SEQUENCE-NUMBER_EXISTING-NAME_PHOTOGRAPH-DESCRIPTION.EXTENSION
//
// This is the reason the feature is worth building rather than reaching for a
// generic renamer: the press tree and the project row already know five of
// those seven fields, so only the description is typed. Editable without a
// deploy via app_settings.commsRenamePresets.
export const HOUSE_PATTERN =
  'Architecture-BRIO_{projectName}_{location}_{photographer}_{n:03}_{name}_{description}';

export const DEFAULT_PRESETS = [
  { name: 'BRIO standard (full protocol)', rules: { mode: 'pattern', pattern: HOUSE_PATTERN } },
  { name: 'BRIO standard, no description', rules: { mode: 'pattern', pattern: 'Architecture-BRIO_{projectName}_{location}_{photographer}_{n:03}_{name}' } },
  { name: 'Short — project, photographer, number', rules: { mode: 'pattern', pattern: '{projectName}_{photographer}_{n:03}' } },
  { name: 'Tidy up (spaces, case, accents)', rules: { mode: 'edit', caseMode: 'title' } },
];

const EMPTY_RULES = {
  mode: 'edit',
  find: '', replace: '', regex: false, caseSensitive: false, replaceAll: true,
  removeFirst: '', removeLast: '',
  caseMode: '',
  prefix: '', suffix: '',
  pattern: HOUSE_PATTERN,
  titleCase: true,
  sequence: false, seqStart: 1, seqStep: 1, seqPad: 3, seqSep: '_', seqPosition: 'suffix',
};

const CASES = [
  { value: '', label: 'Leave as is' },
  { value: 'lower', label: 'lower case' },
  { value: 'upper', label: 'UPPER CASE' },
  { value: 'title', label: 'Title Case' },
];

const STATUS_COLOUR = {
  ok: '#21302A',
  unchanged: '#9A988F',
  collision: '#A3382F',
  invalid: '#A3382F',
};

function Check({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#6E6C66', cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Small({ label, value, onChange, width = 62, type = 'text', placeholder }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ ...S.eyebrow, fontSize: 9 }}>{label}</span>
      <input type={type} value={value ?? ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ ...S.inputStyle, width, padding: '5px 7px', fontFamily: mono, fontSize: 12 }} />
    </label>
  );
}

/**
 * Every file already in the folders this batch touches — the other half of
 * collision detection, since a new name can clash with a file that was never
 * selected. Listings are cached, so this is usually free.
 */
function useFolderNames(assets) {
  const [namesByFolder, setNames] = useState({});
  const folders = useMemo(
    () => [...new Set((assets || []).map(a => a.folderPath || a.path.slice(0, a.path.lastIndexOf('/'))))].sort(),
    [assets],
  );
  const key = folders.join('|');

  useEffect(() => {
    let cancelled = false;
    Promise.all(folders.map(f =>
      listFolder(f).then(entries => [f, (entries || []).filter(e => !e.is_folder).map(e => e.name)])
        .catch(() => [f, []]),
    )).then(pairs => { if (!cancelled) setNames(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return namesByFolder;
}

export function RenameAssetsModal({
  assets, kitSchema = [], ranksById = {}, projectCode, project = null, onClose, onDone, showToast,
}) {
  const [rules, setRules] = useState(EMPTY_RULES);
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [busy, setBusy] = useState(false);
  // Set only when a batch came back short, which keeps the modal open on the
  // report instead of closing over it.
  const [failures, setFailures] = useState(null);
  const [done, setDone] = useState(null);
  // A description belongs to one photograph, so unlike every other field it
  // cannot come from a rule — it is typed per row, keyed by path.
  const [descriptions, setDescriptions] = useState({});
  const namesByFolder = useFolderNames(assets);

  const set = useCallback((k, v) => setRules(r => ({ ...r, [k]: v })), []);

  // Saved presets are a convenience; if the settings read fails the built-in
  // ones still work, so nothing here may throw into the render.
  useEffect(() => {
    Promise.resolve()
      .then(() => supabase.from('app_settings').select('value').eq('key', 'commsRenamePresets').maybeSingle())
      .then(({ data }) => {
        const v = typeof data?.value === 'string' ? JSON.parse(data.value) : data?.value;
        if (Array.isArray(v) && v.length) setPresets(v);
      })
      .catch(() => { /* keep the built-in presets */ });
  }, []);

  const rows = useMemo(
    () => buildRenamePlan(assets, rules, { kitSchema, ranksById, namesByFolder, project, descriptions }),
    [assets, rules, kitSchema, ranksById, namesByFolder, project, descriptions],
  );
  // What the folder's existing names suggest the numbering should carry on
  // from. Offered beside the Start field; never applied without a click.
  const seqSuggestion = useMemo(
    () => suggestSeqStart(assets, rules, { kitSchema, ranksById, namesByFolder, project }),
    [assets, rules, kitSchema, ranksById, namesByFolder, project],
  );
  const wantsDescription = rules.mode === 'pattern' && /\{description\}/.test(rules.pattern || '');
  const summary = useMemo(() => planSummary(rows), [rows]);

  const apply = async () => {
    if (busy || summary.blocked || !summary.ok) return;
    setBusy(true);
    try {
      const renames = rows.filter(r => r.status === 'ok').map(r => ({
        fromPath: r.fromPath, toPath: r.toPath, fileId: r.entry.fileId || null, staged: !!r.staged,
      }));
      const res = await callEdgeFunction('rename-press-files', { renames, projectCode });

      // Patch the cached listings rather than dropping them. Dropping forces a
      // cold re-read that re-resolves every thumbnail in the folder — ten-plus
      // seconds of the old names still on screen after the rename had already
      // landed. The server just told us every new name, so apply them.
      //
      // Only the confirmed ones: a file whose move failed still has its old
      // name, which is what the cache already says.
      // Both paths required, not just ok: a result without them cannot be
      // located in any cache, and reaching into one that isn't there would
      // throw away the failure report we still owe the user.
      const done = (res.results || []).filter(r => r.ok && r.fromPath && r.toPath);
      const byFolder = new Map();
      done.forEach(r => {
        const folder = r.fromPath.slice(0, r.fromPath.lastIndexOf('/'));
        if (!byFolder.has(folder)) byFolder.set(folder, []);
        byFolder.get(folder).push({ from: baseName(r.fromPath), to: baseName(r.toPath) });
      });
      byFolder.forEach((pairs, folder) => renameCachedEntries(folder, pairs));
      // A folder we could not patch (its listing was never cached, or every
      // rename in it failed) still wants the stale entry gone.
      [...new Set(rows.map(r => r.folder))]
        .filter(f => !byFolder.has(f))
        .forEach(f => invalidateListing(f));

      const failed = (res.results || []).filter(r => !r.ok);
      const warned = (res.results || []).filter(r => r.warning);
      if (failed.length) {
        // The modal stays open and names them. A batch of 128 that renames 127
        // is indistinguishable from one that renamed all of them once the
        // dialog has closed and the toast has gone — you would have to know
        // which number to look for.
        setFailures(failed);
        setDone({ renamed: res.renamed, total: renames.length });
        setBusy(false);
        return;
      }
      if (warned.length) {
        showToast?.(`Renamed ${res.renamed}, but some references did not follow`, 'error');
      } else {
        // Names the skipped files too. A batch that renames four of twelve and
        // reports only the four leaves you counting thumbnails to find out what
        // happened to the rest.
        const skipped = rows.length - renames.length;
        showToast?.(skipped
          ? `Renamed ${res.renamed} · ${skipped} already had ${skipped === 1 ? 'its' : 'their'} new name`
          : `Renamed ${res.renamed} file${res.renamed === 1 ? '' : 's'}`);
      }
      onDone?.(res);
    } catch (e) {
      showToast?.(e.message, 'error');
      setBusy(false);
    }
  };

  // Re-sends only what failed. The server's own second sweep already gave each
  // of these the folder to itself once; this is the button for when that was
  // not enough, and it costs one move per file rather than a whole batch.
  const retryFailures = async () => {
    if (busy || !failures?.length) return;
    setBusy(true);
    try {
      const renames = failures.map(f => {
        const row = rows.find(r => r.fromPath === f.fromPath);
        return {
          fromPath: f.fromPath,
          toPath: f.toPath,
          fileId: row?.entry?.fileId || null,
          staged: !!row?.staged,
        };
      });
      const res = await callEdgeFunction('rename-press-files', { renames, projectCode });

      const ok = (res.results || []).filter(r => r.ok && r.fromPath && r.toPath);
      const byFolder = new Map();
      ok.forEach(r => {
        const folder = r.fromPath.slice(0, r.fromPath.lastIndexOf('/'));
        if (!byFolder.has(folder)) byFolder.set(folder, []);
        byFolder.get(folder).push({ from: baseName(r.fromPath), to: baseName(r.toPath) });
      });
      byFolder.forEach((pairs, folder) => renameCachedEntries(folder, pairs));

      const stillFailing = (res.results || []).filter(r => !r.ok);
      setDone(d => ({ renamed: (d?.renamed || 0) + res.renamed, total: d?.total || renames.length }));
      if (stillFailing.length) {
        setFailures(stillFailing);
        setBusy(false);
        return;
      }
      showToast?.(`Renamed the last ${res.renamed} file${res.renamed === 1 ? '' : 's'}`);
      onDone?.(res);
    } catch (e) {
      showToast?.(e.message, 'error');
      setBusy(false);
    }
  };

  const applyPreset = name => {
    const p = presets.find(x => x.name === name);
    if (p) setRules({ ...EMPTY_RULES, ...p.rules });
  };

  const insertToken = t => set('pattern', `${rules.pattern || ''}{${t}}`);

  // A batch that came back short takes over the modal. Everything below it is
  // about composing a rename that has already happened — what matters now is
  // which files did not make it and whether they can be made to.
  if (failures) {
    return (
      <CommsModal title="Rename files" onClose={() => { onDone?.({ renamed: done?.renamed || 0 }); }} width={720}>
        <div style={{ ...S.body, marginBottom: 14 }}>
          Renamed <strong>{done?.renamed ?? 0}</strong> of <strong>{done?.total ?? 0}</strong>.
          {' '}<span style={{ color: '#A3382F' }}>
            {failures.length} did not go through
          </span>{' '}— almost always Dropbox refusing a write while the folder was busy,
          which is a matter of trying again rather than anything wrong with the file.
        </div>
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid rgba(27,27,25,.1)', borderRadius: 3, background: '#FBFAF7' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: 11.5 }}>
            <tbody>
              {failures.map((f, i) => (
                <tr key={f.fromPath || f.toPath || i} style={{ borderBottom: '1px solid rgba(27,27,25,.06)' }}>
                  <td style={{ padding: '5px 8px', color: '#6E6C66', whiteSpace: 'nowrap', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {baseName(f.fromPath) || baseName(f.toPath) || 'a file the server could not name'}
                  </td>
                  <td style={{ padding: '5px 8px', color: '#A3382F', whiteSpace: 'nowrap', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={f.error}>
                    {/* Dropbox's own words, trimmed to the part that means
                        something to a person reading it at speed. */}
                    {/too_many_write_operations/.test(f.error || '')
                      ? 'the folder was busy'
                      : (f.error || 'failed')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ ...S.meta, fontSize: 11.5 }}>
            The {done?.renamed ?? 0} that went through are already renamed — retrying only touches these.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <BtnGhost onClick={() => { onDone?.({ renamed: done?.renamed || 0 }); }}>Leave them</BtnGhost>
            <button onClick={retryFailures} disabled={busy}
              style={{
                fontFamily: mono, fontSize: 12, padding: '7px 14px', borderRadius: 3,
                background: busy ? '#C8C4BB' : GOLD, color: '#F7F6F2', border: 'none',
                cursor: busy ? 'default' : 'pointer',
              }}>
              {busy ? 'Retrying…' : `Retry ${failures.length} file${failures.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </CommsModal>
    );
  }

  return (
    <CommsModal title="Rename files" onClose={onClose} width={720}>
      <div style={{ ...S.body, marginBottom: 14 }}>
        Renaming {assets.length} selected file{assets.length === 1 ? '' : 's'}. Rules apply in a fixed
        order — <span style={{ fontFamily: mono, fontSize: 11.5 }}>find/replace → remove → case → prefix/suffix → sequence</span> —
        and the extension is always kept. <strong>_</strong> separates fields; a space inside a field
        becomes <strong>-</strong>. An empty field drops out rather than leaving a gap.
      </div>

      <Field label="Preset">
        <select defaultValue="" aria-label="Preset" onChange={e => applyPreset(e.target.value)} style={S.inputStyle}>
          <option value="">— start from scratch —</option>
          {presets.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </Field>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <FilterBtn label="Edit the names" active={rules.mode !== 'pattern'} onClick={() => set('mode', 'edit')} />
        <FilterBtn label="Rebuild from a pattern" active={rules.mode === 'pattern'} onClick={() => set('mode', 'pattern')} />
      </div>

      {rules.mode === 'pattern' ? (
        <>
          <Field label="Pattern">
            <input value={rules.pattern} onChange={e => set('pattern', e.target.value)} autoFocus
              aria-label="Pattern" style={{ ...S.inputStyle, fontFamily: mono }} />
          </Field>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
            {TOKENS.map(t => (
              <button key={t} onClick={() => insertToken(t)} title={`Insert {${t}}`}
                style={{ fontFamily: mono, fontSize: 11, padding: '4px 7px', borderRadius: 3, cursor: 'pointer',
                  border: '1px solid rgba(27,27,25,.18)', background: '#F7F6F2', color: '#6E6C66' }}>
                {`{${t}}`}
              </button>
            ))}
            <span style={{ ...S.meta, fontSize: 11, alignSelf: 'center', marginLeft: 4 }}>
              {'{n:03}'} pads the number
            </span>
          </div>
          {hasSequenceToken(rules.pattern) && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
              <Small label="Start at" value={rules.seqStart} onChange={v => set('seqStart', v)} />
              {/* A suggestion, never applied on its own. Carrying on from the
                  numbers already in the folder is usually what you want when a
                  shoot is renamed in more than one pass — but re-numbering a
                  whole folder from 1 is a legitimate thing to do, so this only
                  offers. */}
              {seqSuggestion !== null && Number(rules.seqStart) !== seqSuggestion && (
                <button
                  onClick={() => set('seqStart', seqSuggestion)}
                  title="The numbers already used in this folder carry on from here"
                  style={{ ...S.meta, fontSize: 11.5, padding: '4px 8px', borderRadius: 3, cursor: 'pointer',
                    border: `1px solid ${GOLD}`, background: 'transparent', color: GOLD, marginBottom: 1 }}>
                  Continue the folder at {String(seqSuggestion).padStart(3, '0')}
                </button>
              )}
              {seqSuggestion === null && (
                <span style={{ ...S.meta, fontSize: 11, marginBottom: 5 }}>
                  nothing numbered in this folder yet
                </span>
              )}
            </div>
          )}
          <div style={{ marginBottom: 14, marginTop: -4 }}>
            <Check label="Title Case the values (acronyms like DSC and BRIO kept as they are)"
              checked={rules.titleCase !== false} onChange={v => set('titleCase', v)} />
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Find"><input value={rules.find} onChange={e => set('find', e.target.value)} autoFocus
              aria-label="Find" style={{ ...S.inputStyle, fontFamily: mono }} /></Field>
            <Field label="Replace with"><input value={rules.replace} onChange={e => set('replace', e.target.value)}
              aria-label="Replace with" style={{ ...S.inputStyle, fontFamily: mono }} /></Field>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14, marginTop: -4 }}>
            <Check label="Regular expression" checked={rules.regex} onChange={v => set('regex', v)} />
            <Check label="Match case" checked={rules.caseSensitive} onChange={v => set('caseSensitive', v)} />
            <Check label="First match only" checked={!rules.replaceAll} onChange={v => set('replaceAll', !v)} />
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <Small label="Drop first" value={rules.removeFirst} onChange={v => set('removeFirst', v)} width={70} placeholder="0" />
            <Small label="Drop last" value={rules.removeLast} onChange={v => set('removeLast', v)} width={70} placeholder="0" />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ ...S.eyebrow, fontSize: 9 }}>Case</span>
              <select value={rules.caseMode} aria-label="Case" onChange={e => set('caseMode', e.target.value)}
                style={{ ...S.inputStyle, width: 140, padding: '5px 7px' }}>
                {CASES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <Small label="Add before" value={rules.prefix} onChange={v => set('prefix', v)} width={110} />
            <Small label="Add after" value={rules.suffix} onChange={v => set('suffix', v)} width={110} />
          </div>
        </>
      )}

      <div style={{ borderTop: '1px solid rgba(27,27,25,.1)', paddingTop: 12, marginBottom: 14 }}>
        <Check label="Number them" checked={rules.sequence} onChange={v => set('sequence', v)} />
        {rules.sequence && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
            <Small label="Start at" value={rules.seqStart} onChange={v => set('seqStart', v)} />
            <Small label="Step" value={rules.seqStep} onChange={v => set('seqStep', v)} />
            <Small label="Digits" value={rules.seqPad} onChange={v => set('seqPad', v)} />
            <Small label="Separator" value={rules.seqSep} onChange={v => set('seqSep', v)} width={70} />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ ...S.eyebrow, fontSize: 9 }}>Position</span>
              <select value={rules.seqPosition} onChange={e => set('seqPosition', e.target.value)}
                style={{ ...S.inputStyle, width: 110, padding: '5px 7px' }}>
                <option value="suffix">At the end</option>
                <option value="prefix">At the start</option>
              </select>
            </label>
            <span style={{ ...S.meta, fontSize: 11, paddingBottom: 6 }}>Restarts in each folder</span>
          </div>
        )}
      </div>

      <div style={{ ...S.eyebrow, fontSize: 9, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span>Preview</span>
        {wantsDescription && <span style={{ textTransform: 'none', letterSpacing: 0 }}>Describe each photograph — blank is fine</span>}
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid rgba(27,27,25,.1)', borderRadius: 3, background: '#FBFAF7' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: mono, fontSize: 11.5 }}>
          <tbody>
            {rows.slice(0, 200).map(r => (
              <tr key={r.fromPath} style={{ borderBottom: '1px solid rgba(27,27,25,.06)' }}>
                <td style={{ padding: '5px 8px', color: '#9A988F', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.from}
                </td>
                {wantsDescription && (
                  <td style={{ padding: '3px 6px', width: 170 }}>
                    {/* Undefined means untouched, so a description recovered from a
                        previous run shows through and can be edited. An empty string
                        is a decision — clearing the box has to stick rather than
                        snapping back to the old text. */}
                    <input
                      value={descriptions[r.fromPath] ?? r.priorDescription ?? ''}
                      aria-label={`Description for ${r.from}`}
                      placeholder="description…"
                      onChange={e => setDescriptions(d => ({ ...d, [r.fromPath]: e.target.value }))}
                      style={{ ...S.inputStyle, fontFamily: mono, fontSize: 11, padding: '3px 6px' }} />
                  </td>
                )}
                <td style={{ padding: '5px 4px', color: '#C8C4BB', width: 18 }}>→</td>
                <td style={{ padding: '5px 8px', color: STATUS_COLOUR[r.status], wordBreak: 'break-all' }}>
                  {r.status === 'unchanged' ? 'unchanged' : r.to}
                  {r.note && <span style={{ color: '#A3382F' }}> — {r.note}</span>}
                  {r.staged && r.status === 'ok' && <span style={{ color: '#9A988F' }}> · via a temporary name</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 200 && (
          <div style={{ ...S.meta, fontSize: 11, padding: '6px 8px' }}>…and {rows.length - 200} more.</div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <span style={{ ...S.meta, fontSize: 11.5, color: summary.blocked ? '#A3382F' : '#6E6C66' }}>
          {summary.blocked
            ? `${summary.collision + summary.invalid} problem${summary.collision + summary.invalid === 1 ? '' : 's'} — fix these before renaming`
            /* Spelled out against the selection, because the gap between the
               two is the whole question when a batch comes back smaller than
               you expected. "8 to rename" alone reads as agreement with a
               selection of twelve. */
            : summary.unchanged
              ? `${summary.ok} of ${summary.total} selected — ${summary.unchanged} already ${summary.unchanged === 1 ? 'has' : 'have'} this name and will be left alone`
              : `${summary.ok} to rename`}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          <BtnGhost onClick={onClose}>Cancel</BtnGhost>
          <button onClick={apply} disabled={busy || summary.blocked || !summary.ok}
            style={{
              background: (summary.blocked || !summary.ok) ? '#C8C4BB' : GOLD, color: '#F7F6F2', border: 'none',
              borderRadius: 3, padding: '8px 16px', fontFamily: mono, fontSize: 12,
              cursor: (busy || summary.blocked || !summary.ok) ? 'default' : 'pointer',
            }}>
            {busy
              ? 'Renaming…'
              : summary.ok === summary.total
                ? `Rename ${summary.ok} file${summary.ok === 1 ? '' : 's'}`
                : `Rename ${summary.ok} of ${summary.total}`}
          </button>
        </div>
      </div>
    </CommsModal>
  );
}
